import { useGlobal } from "@/app/providers/use-global";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useScreen } from "@/hooks/use-screen";
import { cn } from "@/lib/utils";
import { CopyableText } from "@/shared/components/copyable-text";
import type { ExpandedModel } from "@/shared/data/models";
import { useTextModels } from "@/shared/hooks/use-models";
import { Check, ChevronsUpDown, Circle } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ignoreModelIds = [
  // image models
  "llama-3.2-11b-vision-preview",
  "llama-3.2-90b-vision-preview",
  "llava-v1.5-7b-4096-preview",

  // non-standard chat models
  "meta-llama/llama-guard-4-12b",
  "llama3-groq-70b-8192-tool-use-preview",
  "llama3-groq-8b-8192-tool-use-preview",
  "meta-llama/llama-guard-4-12b",

  // audio models
  "whisper-large-v3",
  "whisper-large-v3-turbo",
  "whisper-large-v3-arabic",
  "playtts-dev",
  "playai-tts-arabic",
  "playai-tts",
];

export const DEFAULT_MODEL = "groq/compound-mini" as string;

type ModelSelectProps = {
  triggerClassName?: string;
};

export default function ModelSelect({ triggerClassName }: ModelSelectProps) {
  const { currentModel, setCurrentModel } = useGlobal();
  const { data: modelsData, error: modelsError } = useTextModels();
  const [open, setOpen] = useState(false);
  const models = modelsData?.data || [];
  const { width, isMobile } = useScreen();

  const modelsPopupAlignOffset = useMemo(() => {
    if (width > 400) {
      return 0;
    }
    if (width <= 360) {
      return -(width - 300) / 2 + 2;
    }
    return -(width - 300) / (width / 100) + 2;
  }, [width, isMobile]);

  const onModelChange = useCallback(
    (model: ExpandedModel) => {
      setCurrentModel(model);
    },
    [setCurrentModel],
  );

  const groups: [string, ExpandedModel[]][] = useMemo(() => {
    if (!models) {
      return [];
    }
    const groupsObject: Record<string, ExpandedModel[]> = {};
    models
      .filter((model) => !ignoreModelIds.includes(model.id))
      .forEach((model) => {
        if (!groupsObject[model.owned_by]) {
          groupsObject[model.owned_by] = [];
        }
        groupsObject[model.owned_by].push(model);
      });
    const sortedGroups: [string, ExpandedModel[]][] = Object.keys(groupsObject)
      .sort((a, b) => {
        return a.localeCompare(b);
      })
      .map((k) => {
        return [
          k,
          groupsObject[k].sort((a: ExpandedModel, b: ExpandedModel) =>
            a.timestamp && b.timestamp
              ? b.timestamp - a.timestamp // Sort by timestamps from newer to older
              : 0,
          ),
        ];
      });
    return sortedGroups;
  }, [models]);

  useEffect(() => {
    if (modelsError) {
      setCurrentModel(null);
      return;
    }
    if (models && models.length > 0) {
      if (!models.find((model) => model.id === currentModel?.id)) {
        onModelChange(
          models.find((model) => model.id === DEFAULT_MODEL) || models[0],
        );
      }
    }
  }, [onModelChange, currentModel, models]);

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        if (models.length > 0 || open !== newOpen) {
          setOpen(newOpen);
        }
      }}
    >
      <PopoverTrigger
        className={triggerClassName}
        asChild={true}
        disabled={groups.length === 0}
      >
        <Button
          variant="outline"
          className="max-w-48 md:max-w-72 flex items-center justify-between border-secondary"
        >
          {currentModel !== undefined && groups.length === 0 ? (
            <p className="truncate normal-case">No models available</p>
          ) : (
            <p className="truncate">
              {currentModel ? getModelOptionLabel(currentModel) : ""}
            </p>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        alignOffset={modelsPopupAlignOffset}
        className="w-[300px] md:w-[400px] p-0 border dark:border-muted shadow-md"
      >
        <Command
          loop={true}
          className="bg-background"
          style={{ maxHeight: "min(24rem, 80svh)" }}
          filter={(value, search) => {
            return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput
            placeholder="Search Models..."
            className="border-0 shadow-none ring-0 focus:border-transparent focus:shadow-none focus:outline-none focus:ring-0"
          />
          <CommandList>
            {groups.map(([type, ms]) => (
              <CommandGroup key={type} heading={type} className="p-0">
                {ms.map((model) => (
                  <ModelItem
                    key={model.id}
                    model={model}
                    isSelected={currentModel?.id === model.id}
                    disabled={!model.active}
                    onSelect={() => {
                      if (model) {
                        onModelChange(model);
                      }
                      setOpen(false);
                    }}
                  />
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
        {models.find((model) => !model.active) && (
          <div className="">
            <Separator className="" />
            <div className="opacity-80 p-3">Some models are offline</div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// utils
const getModelOptionLabel = (model: ExpandedModel) => {
  // Use display name if available, otherwise fallback to lowercase model ID
  const displayName = model.metadata?.display_name || model.id.toLowerCase();

  let status = "";
  if (!model.active) {
    status = ": Unavailable";
  }
  if (!model.active && model.id === "llama2-70b-4096") {
    status = ": Decommissioned";
  }
  return `${displayName}${status}`;
};

interface ModelItemProps {
  model: ExpandedModel;
  isSelected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

const ModelHoverCard = ({
  model,
  children,
}: {
  model: ExpandedModel;
  children: React.ReactNode;
}) => {
  return (
    <HoverCard key={model.id} openDelay={100} closeDelay={0}>
      <HoverCardTrigger className="ml-auto">{children}</HoverCardTrigger>
      <HoverCardContent align="start" className="w-[200px]" side="left">
        <div className="space-y-2">
          <CopyableText
            text={model.id}
            textClassName="text-foreground font-mono text-xs font-medium"
            alwaysShowCopyButton={true}
          />
          {model.description && (
            <p className="text-muted-foreground font-inter text-xs leading-[18px] tracking-normal">
              {model.description}
            </p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

function ModelItem({ model, isSelected, disabled, onSelect }: ModelItemProps) {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <ModelHoverCard key={model.id} model={model}>
      <CommandItem
        key={model.id}
        onSelect={onSelect}
        disabled={disabled}
        ref={ref}
        value={model.id}
        className={cn("flex gap-2", isSelected && "bg-secondary")}
      >
        <div
          className={cn(
            "flex-1 overflow-hidden whitespace-pre text-ellipsis",
            disabled ? "opacity-80" : "",
          )}
        >
          {getModelOptionLabel(model)}
        </div>
        {disabled && (
          <Circle color="red" fill="red" className="h-2 w-2 ml-auto" />
        )}
        {isSelected && <Check className={cn("ml-auto h-4 w-4")} />}
      </CommandItem>
    </ModelHoverCard>
  );
}
